import { XCircle } from '@untitledui/icons';
import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { CloseButton } from '@/lib/base/buttons/close-button';

/**
 * Maps the `hubspot_error` query param set by the backend OAuth callback
 * (apps/hubspot_sync/views/oauth_views.py) to user-facing copy.
 *
 * Single source of truth for every page the callback can redirect to
 * (`/settings/hubspot` and `/settings/import`), so the two surfaces never drift.
 */
const HUBSPOT_ERROR_MESSAGES: Record<string, string> = {
  oauth_failed: "We couldn't complete HubSpot authentication. Try again.",
  user_mismatch:
    'The HubSpot account is associated with a different user. Please try again.',
  admin_required:
    'You must be a HubSpot account administrator to connect. Please try again with an admin account.',
  not_enabled: 'HubSpot integration is not enabled for this organization.',
  token_exchange_failed:
    "We couldn't exchange the authorization code. Try connecting again.",
  portal_lookup_failed:
    "We couldn't retrieve your HubSpot portal information. Try again.",
  connect_race:
    'Another connection attempt is already in progress. Please wait and try again.',
  state_expired:
    'The connection request took too long and expired. Please try connecting again.',
};

const DEFAULT_HUBSPOT_ERROR_MESSAGE =
  'An unexpected error occurred. Please try connecting again.';

/**
 * Renders the "We couldn't connect HubSpot" banner when the current URL carries
 * a `hubspot_error` query param, and strips the param on dismiss. Renders
 * nothing otherwise, so it is safe to mount unconditionally.
 */
export function HubspotOauthErrorBanner() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [oauthError, setOauthError] = useState<string | null>(null);

  useEffect(() => {
    setOauthError(searchParams.get('hubspot_error'));
  }, [searchParams]);

  const dismissError = useCallback(() => {
    setOauthError(null);
    setSearchParams(
      prev => {
        const nextParams = new URLSearchParams(prev);
        nextParams.delete('hubspot_error');
        return nextParams;
      },
      { replace: true }
    );
  }, [setSearchParams]);

  if (!oauthError) {
    return null;
  }

  return (
    <div className='rounded-lg border border-utility-error-200 bg-utility-error-50 p-4'>
      <div className='flex items-start gap-3'>
        <XCircle className='mt-0.5 h-5 w-5 shrink-0 text-utility-error-600' />
        <div className='flex-1'>
          <p className='text-sm font-semibold text-utility-error-900'>
            We couldn't connect HubSpot
          </p>
          <p className='mt-1 text-sm text-utility-error-700'>
            {HUBSPOT_ERROR_MESSAGES[oauthError] ??
              DEFAULT_HUBSPOT_ERROR_MESSAGE}
          </p>
        </div>
        <CloseButton
          onPress={dismissError}
          label='Dismiss'
          size='sm'
          className='-my-1.5 -mr-1.5 shrink-0'
        />
      </div>
    </div>
  );
}
